import { useState, type ClipboardEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupportAttachment } from '@ml/shared/support-files';
import { api } from '../../lib/api';
import { AttachmentPicker, SupportAttachments, useAttachmentSelection, useFileDrop } from './Attachments';
import { ImageEditor } from './ImageEditor';
import { clipboardImages, replaceFile, supportForm } from './attachment-rules';
import './support-conversation.css';

export const supportStatuses = [['open','Under review'],['planned','Planned'],['needs_info','Needs more information'],['in_progress','Building'],['resolved','Shipped'],['closed','Closed']] as const;
export function supportStatusLabel(status:string,kind:string) {
  if(kind!=='feature'&&status==='in_progress')return 'In progress';
  if(kind!=='feature'&&status==='resolved')return 'Resolved';
  return supportStatuses.find(([value])=>value===status)?.[1]??status;
}
type Comment={id:string;is_staff:number;body:string;created_at:number;attachments?:SupportAttachment[]};
export function SupportConversation({id}:{id:string}) {
  const cache=useQueryClient();
  const [message,setMessage]=useState(''),[saving,setSaving]=useState(false),[error,setError]=useState('');
  const files=useAttachmentSelection(),drop=useFileDrop(files.add,saving);
  const [editing,setEditing]=useState<number|null>(null);
  const query=useQuery({queryKey:['support-comments',id],queryFn:()=>api<{items:Comment[]}>(`/api/support/${id}/comments`),refetchInterval:30000});
  // Pasted screenshots (a snip, a copied image) attach straight to the reply; text pastes are left alone.
  function paste(event:ClipboardEvent<HTMLTextAreaElement>){const images=clipboardImages(event.clipboardData);if(!images.length)return;event.preventDefault();files.add(images);}
  async function post(){
    if(saving||(!message.trim()&&!files.files.length))return;
    setSaving(true);setError('');
    try{
      await api(`/api/support/${id}/comments`,{method:'POST',body:files.files.length?supportForm({body:message},files.files):{body:message}});
      setMessage('');files.clear();await cache.invalidateQueries({queryKey:['support-comments',id]});
    }
    catch(e){setError(e instanceof Error?e.message:'Could not post comment.');}
    finally{setSaving(false);}
  }
  return <div className="gfp-support-conversation">
    <h3>Conversation</h3>
    {query.isPending&&<p role="status">Loading conversation…</p>}
    {query.isError&&<p role="alert">{query.error.message} <button type="button" onClick={()=>void query.refetch()}>Try again</button></p>}
    {query.data?.items.length===0&&<p>Share details or ask a question here. Comments are visible to the requester and the team.</p>}
    {query.data?.items.map(comment=><article key={comment.id}><strong>{comment.is_staff?'Magically Linked team':'Requester'}</strong> <time dateTime={new Date(comment.created_at).toISOString()}>{new Date(comment.created_at).toLocaleString()}</time>{comment.body&&<p>{comment.body}</p>}<SupportAttachments items={comment.attachments}/></article>)}
    <form onSubmit={event=>{event.preventDefault();void post();}}>
      <label>Add a comment<textarea className={drop.over?'is-over':undefined} value={message} onChange={event=>setMessage(event.target.value)} onPaste={paste} {...drop.props} maxLength={12000} rows={3} disabled={saving} placeholder="Reply, or paste a screenshot"/></label>
      <AttachmentPicker zone={false} files={files.files} error={files.error} onAdd={files.add} onRemove={files.remove} onEdit={setEditing} disabled={saving}/>
      {error&&<p role="alert">{error}</p>}
      <button type="submit" disabled={saving||(!message.trim()&&!files.files.length)}>{saving?'Posting…':'Post comment'}</button>
    </form>
    {editing!==null&&files.files[editing]&&<ImageEditor file={files.files[editing]} onCancel={()=>setEditing(null)} onDone={edited=>{files.update(current=>replaceFile(current,editing,edited));setEditing(null);}}/>}
  </div>;
}
